
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PlatformStates } from "../types";
import { platformConfigurations } from "../../config/platformConfig";
import { toast } from "sonner";
import { databaseMonitor } from "@/utils/performance/databaseMonitoring";

export const useCredentialsLoader = () => {
  const [platformStates, setPlatformStates] = useState<PlatformStates>(
    Object.fromEntries(
      platformConfigurations.map(platform => [
        platform.id,
        { enabled: false, credentials: {}, status: 'idle' }
      ])
    )
  );
  const [loadingCredentials, setLoadingCredentials] = useState(true);

  useEffect(() => {
    loadSavedCredentials();
  }, []);

  const loadSavedCredentials = async () => {
    try {
      setLoadingCredentials(true);
      console.log('Loading saved credentials...');
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('No authenticated user found');
        setLoadingCredentials(false);
        return;
      }
      
      console.log('Loading credentials for user:', user.id);
      
      // Get user's organization
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      console.log('User organization:', userProfile?.organization_id);
      
      // First, try to get credentials for this specific user
      let { data, error } = await supabase
        .from('platform_credentials')
        .select('platform, is_active, credentials')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Database error loading user credentials:', error);
        databaseMonitor.logError(error.message, {
          table: 'platform_credentials',
          context: 'SELECT user credentials'
        });
        throw error;
      }
      
      console.log('Loaded user credentials:', data?.length || 0, 'records');
      
      // If user has no credentials but is in an organization, check for organization credentials
      if ((!data || data.length === 0) && userProfile?.organization_id) {
        console.log('No user credentials found, checking organization credentials...');
        
        // Get all active credentials from users in the same organization
        const { data: orgCredentials, error: orgError } = await supabase
          .from('platform_credentials')
          .select(`
            platform, 
            is_active, 
            credentials, 
            user_id,
            profiles!inner(organization_id)
          `)
          .eq('is_active', true)
          .eq('profiles.organization_id', userProfile.organization_id);

        if (orgError) {
          console.error('Database error loading org credentials:', orgError);
        } else if (orgCredentials && orgCredentials.length > 0) {
          console.log('Found organization credentials:', orgCredentials.length, 'records');
          data = orgCredentials.map(cred => ({
            platform: cred.platform,
            is_active: cred.is_active,
            credentials: cred.credentials
          }));
        }
      }
      
      if (data && data.length > 0) {
        const updatedStates = { ...platformStates };
        
        data.forEach(credential => {
          console.log('Processing credential for platform:', credential.platform);
          
          if (updatedStates[credential.platform]) {
            let maskedCredentials: Record<string, string> = {};
            
            const platform = platformConfigurations.find(p => p.id === credential.platform);
            if (platform) {
              platform.fields.forEach(field => {
                const credentialObj = credential.credentials as Record<string, any>;
                if (field.name in credentialObj && credentialObj[field.name]) {
                  maskedCredentials[field.name] = '••••••••••••••••';
                }
              });
            }
              
            updatedStates[credential.platform] = {
              ...updatedStates[credential.platform],
              enabled: credential.is_active,
              status: credential.is_active ? 'valid' : 'idle',
              credentials: maskedCredentials
            };
            
            console.log('Updated state for platform:', credential.platform, {
              enabled: credential.is_active,
              hasCredentials: Object.keys(maskedCredentials).length > 0
            });
          }
        });
        
        setPlatformStates(updatedStates);
      } else {
        console.log('No saved credentials found for user or organization');
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Log different types of errors for debugging
      if (errorMessage.includes('permission')) {
        toast.error('Permission denied: Unable to load credentials');
      } else if (errorMessage.includes('network')) {
        toast.error('Network error: Please check your connection');
      } else {
        toast.error(`Failed to load saved credentials: ${errorMessage}`);
      }
      
      databaseMonitor.logError(errorMessage, {
        table: 'platform_credentials',
        context: 'load_saved_credentials'
      });
    } finally {
      setLoadingCredentials(false);
    }
  };

  return {
    platformStates,
    setPlatformStates,
    loadingCredentials,
    refreshCredentials: loadSavedCredentials
  };
};
